// Picture pickers shared by Create Room, Room Settings and Practice: map cards with thumbnails
// and mode cards with icons. Each card is a button (role=radio) so every device can pick.
import { ARENAS, ModeId, type MapId } from '@shared/ids';
import { icon, MODE_ICONS } from '../icons';
import { MODE_BLURBS, MODE_NAMES, mapName } from '../labels';
import { mapThumb } from '../mapThumbs';
import { el, setAttr } from '../ui';

export interface Picker<T> { el: HTMLElement; get(): T; set(v: T): void }

function picker<T>(label: string, cls: string, items: readonly { value: T; node: HTMLElement[] }[], value: T, onChange: (v: T) => void): Picker<T> {
  let v = value;
  const root = el('div', { class: `pick-grid ${cls}`, attrs: { role: 'radiogroup', 'aria-label': label } });
  const btns = items.map((it) => {
    const b = el('button', { class: 'pick-card', attrs: { type: 'button', role: 'radio', 'aria-checked': 'false' } }, ...it.node);
    b.addEventListener('click', () => {
      if (v !== it.value) {
        v = it.value;
        render();
        onChange(v);
      }
    });
    root.append(b);
    return b;
  });
  const render = (): void => btns.forEach((b, i) => setAttr(b, 'aria-checked', String(items[i].value === v)));
  render();
  return { el: root, get: () => v, set(nv) { v = nv; render(); } };
}

/** Map cards (arenas), optionally with a leading "Rotation" card. */
export function mapPicker(value: MapId | 'rotation', onChange: (v: MapId | 'rotation') => void, withRotation: boolean): Picker<MapId | 'rotation'> {
  const items: { value: MapId | 'rotation'; node: HTMLElement[] }[] = [];
  if (withRotation) {
    items.push({ value: 'rotation', node: [el('span', { class: 'pick-art rotation' }, icon('rematch')), el('span', { class: 'pick-name', text: 'Rotation' })] });
  }
  for (const id of ARENAS) items.push({ value: id, node: [el('span', { class: 'pick-art' }, mapThumb(id)), el('span', { class: 'pick-name', text: mapName(id) })] });
  return picker('Map', 'maps', items, value, onChange);
}

const SCORING_MODES: readonly ModeId[] = [ModeId.ROYALE, ModeId.HOT_POTATO, ModeId.CROWN_GRAB];

/** Mode cards with icon, name and one-line rules. */
export function modePicker(value: ModeId, onChange: (v: ModeId) => void): Picker<ModeId> {
  const items = SCORING_MODES.map((m) => ({
    value: m,
    node: [el('span', { class: 'pick-icon' }, icon(MODE_ICONS[m])), el('span', { class: 'pick-name', text: MODE_NAMES[m] }), el('span', { class: 'pick-blurb', text: MODE_BLURBS[m] })],
  }));
  return picker('Mode', 'modes', items, value, onChange);
}
